<template>
    <div class="flex h-auto bg-gray-900 text-white">
      <!-- Sidebar -->
      <aside class="w-64 bg-gray-800 p-6 flex flex-col space-y-6 shadow-lg bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700">
        <div class="flex items-center justify-center space-x-3">
       <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-20">
        </div>
        <Navbar></Navbar>
      </aside>
  
      <div class="flex-1 flex flex-col">
        <header class="h-16 bg-gray-800 flex items-center justify-between px-6 shadow-md bg-gradient-to-b from-gray-800 to-gray-900 border-b border-gray-700">
          <h2 class="text-xl font-semibold">Load Balancers</h2>
          <button @click="showAddModal = true" class="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded">+ Add Load Balancer</button>
        </header>
  
        <main class="p-6 flex-1">
            <div class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
            rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition">
            <h3 class="text-lg font-semibold mb-4">Active Load Balancers</h3>
  
            <table class="w-full text-left border-collapse">
              <thead>
                <tr class="bg-gray-700">
                  <th class="p-3">ID</th>
                  <th class="p-3">IP</th>
                  <th class="p-3">Port</th>
                  <th class="p-3">Protocol</th>
                  <th class="p-3">Algorithm</th>
                  <th class="p-3">Status</th>
                  <th class="p-3">Connected Servers</th>
                  <th class="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="lb in loadBalancers" :key="lb.id" class="border-b border-gray-600">
                  <td class="p-3">{{ lb.id }}</td>
                  <td class="p-3">{{ lb.ip }}</td>
                  <td class="p-3">{{ lb.port }}</td>
                  <td class="p-3">{{ lb.protocol }}</td>
                  <td class="p-3">{{ lb.algorithm }}</td>
                  <td class="p-3">
                    <span :class="{'text-green-500': lb.status === 'Active', 'text-red-500': lb.status === 'Inactive'}">
                      {{ lb.status }}
                    </span>
                  </td>
                  <td class="p-3">{{ Array.isArray(lb.servers) ? lb.servers.join(", ") : JSON.parse(lb.servers).join(", ") }}</td>
                  <td class="p-3 flex space-x-2">
                    <button class="px-3 py-1 bg-yellow-500 rounded hover:bg-yellow-600" @click="editLoadBalancer(lb)">Edit</button>
                    <button class="px-3 py-1 bg-red-500 rounded hover:bg-red-600" @click="confirmDelete(lb.id)">Delete</button>
                    <button class="px-3 py-1 bg-gray-500 rounded hover:bg-gray-600" @click="toggleStatus(lb)">
                      {{ lb.status === 'Active' ? 'Deactivate' : 'Activate' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </main>
      </div>
  
      <!-- Add/Edit Modal -->
      <div v-if="showAddModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div class="bg-gray-800 p-6 rounded-lg w-96">
          <h3 class="text-lg font-semibold mb-4">{{ editing ? 'Edit Load Balancer' : 'Add Load Balancer' }}</h3>
          <input type="text" v-model="loadBalancerData.ip" placeholder="IP Address" class="w-full p-2 rounded bg-gray-700 mb-3">
          <input type="number" v-model="loadBalancerData.port" placeholder="Port" class="w-full p-2 rounded bg-gray-700 mb-3">
          <select v-model="loadBalancerData.protocol" class="w-full p-2 rounded bg-gray-700 mb-3">
            <option value="HTTP">HTTP</option>
            <option value="HTTPS">HTTPS</option>
            <option value="TCP">TCP</option>
          </select>
          <select v-model="loadBalancerData.algorithm" class="w-full p-2 rounded bg-gray-700 mb-3">
            <option value="Round Robin">Round Robin</option>
            <option value="Least Connections">Least Connections</option>
            <option value="IP Hash">IP Hash</option>
          </select>
          <input type="text" v-model="loadBalancerData.servers" placeholder="Server IDs (comma separated)" class="w-full p-2 rounded bg-gray-700 mb-3">
          <button @click="editing ? updateLoadBalancer() : addLoadBalancer()" class="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded w-full">
            {{ editing ? 'Update' : 'Add' }}
          </button>
          <button @click="showAddModal = false" class="mt-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 rounded w-full">Cancel</button>
        </div>
      </div>
  
      <!-- Confirmation Modal -->
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div class="bg-gray-800 p-6 rounded-lg w-96">
          <h3 class="text-lg font-semibold mb-4">Confirm Deletion</h3>
          <p class="mb-4">Are you sure you want to delete this load balancer?</p>
          <button @click="deleteLoadBalancer(confirmedDeleteId)" class="px-4 py-2 bg-red-500 hover:bg-red-600 rounded w-full">Delete</button>
          <button @click="showConfirmModal = false" class="mt-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 rounded w-full">Cancel</button>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import Navbar from '@/components/nav.vue';
  import axios from 'axios';
  
  export default {
    name: "LoadBalancersPage",
    data() {
      return {
        loadBalancers: [],
        showAddModal: false,
        showConfirmModal: false,
        confirmedDeleteId: null,
        editing: false,
        loadBalancerData: {
          id: null,
          ip: "",
          port: 80,
          protocol: "HTTP",
          algorithm: "Round Robin",
          servers: ""
        }
      };
    },
    methods: {
      async fetchLoadBalancers() {
        try {
          const res = await axios.get('https://api.syniac.com:3000/api/load-balancer/load-balancers', { withCredentials: true });
          this.loadBalancers = res.data;
        } catch (error) {
          console.error("Error fetching load balancers:", error);
        }
      },
      async addLoadBalancer() {
        try {
          const res = await axios.post('https://api.syniac.com:3000/api/load-balancer/load-balancers', {
            ...this.loadBalancerData,
            servers: this.loadBalancerData.servers.split(",").map(s => s.trim()),
            status: "Active"
          }, { withCredentials: true });
  
          this.loadBalancers.push(res.data);
          this.showAddModal = false;
        } catch (error) {
          console.error("Error adding load balancer:", error);
        }
      },
      editLoadBalancer(lb) {
        this.loadBalancerData = { ...lb, servers: Array.isArray(lb.servers) ? lb.servers.join(", ") : JSON.parse(lb.servers).join(", ") };
        this.editing = true;
        this.showAddModal = true;
      },
      async updateLoadBalancer() {
        try {
            await axios.put(`https://api.syniac.com:3000/api/load-balancer/load-balancers/${this.loadBalancerData.id}`, {
            ip: this.loadBalancerData.ip,
            port: this.loadBalancerData.port,
            protocol: this.loadBalancerData.protocol,
            algorithm: this.loadBalancerData.algorithm,
            servers: this.loadBalancerData.servers.split(",").map(s => s.trim())
            }, { withCredentials: true });

            this.fetchLoadBalancers();
            this.showAddModal = false;
            this.resetForm();
        } catch (error) {
            console.error("Error updating load balancer:", error);
        }
      },
      async deleteLoadBalancer(id) {
        try {
          await axios.delete(`https://api.syniac.com:3000/api/load-balancer/load-balancers/${id}`, { withCredentials: true });
          this.loadBalancers = this.loadBalancers.filter(lb => lb.id !== id);
          this.showConfirmModal = false;
        } catch (error) {
          console.error("Error deleting load balancer:", error);
        }
      },
      confirmDelete(id) {
        this.confirmedDeleteId = id;
        this.showConfirmModal = true;
      },
      async toggleStatus(lb) {
        try {
          const newStatus = lb.status === "Active" ? "Inactive" : "Active";
          await axios.patch(`https://api.syniac.com:3000/api/load-balancer/load-balancers/${lb.id}`, { status: newStatus }, { withCredentials: true });
          lb.status = newStatus;
        } catch (error) {
          console.error("Error toggling status:", error);
        }
    }
    },
    mounted() {
      this.fetchLoadBalancers();
    },
    components: { Navbar }
  };
  </script>
  
